import type { UnifiedLightboxTrackingEvents } from '@components/unified-lightbox/hooks/use-track-unified-lightbox';
import { CarouselWithStaticVisibleSlots } from '@tkww/orion-web-ds-structure';
import React, {
	type MouseEvent,
	type ReactNode,
	useCallback,
	useEffect,
} from 'react';
import type { FCWithChildren } from 'types/react-extended';
import useCarouselSize from '../../hooks/useCarouselSize';

interface ArrowProps {
	onClick?: (e: MouseEvent) => void;
}

export interface CarouselProps {
	activeImageIndex: number;
	children: ReactNode;
	mediaLength: number;
	renderNextButton: ({ onClick }: ArrowProps) => JSX.Element | null;
	renderPreviousButton: ({ onClick }: ArrowProps) => JSX.Element | null;
	onNavigationChange: (index: number) => void;
	trackingEvents?: UnifiedLightboxTrackingEvents;
}

const Carousel: FCWithChildren<CarouselProps> = (props) => {
	const {
		activeImageIndex,
		mediaLength,
		children,
		renderNextButton,
		renderPreviousButton,
		onNavigationChange,
	} = props;

	const keydown = useCallback(
		(imageIndex: number) => {
			return (event: KeyboardEvent) => {
				switch (event.key) {
					case 'ArrowLeft': {
						if (imageIndex > 0) {
							onNavigationChange(imageIndex - 1);
						}
						break;
					}

					case 'ArrowRight': {
						if (imageIndex < mediaLength - 1) {
							onNavigationChange(imageIndex + 1);
						}
						break;
					}
					default:
						break;
				}
			};
		},
		[mediaLength, onNavigationChange],
	);

	useEffect(() => {
		const keydownEvent = keydown(activeImageIndex);
		window.addEventListener('keydown', keydownEvent);
		return () => {
			window.removeEventListener('keydown', keydownEvent);
		};
	}, [activeImageIndex, keydown]);

	return (
		<CarouselWithStaticVisibleSlots
			visibleSlots={1}
			navigationIndex={activeImageIndex}
			onNavigationIndexChange={onNavigationChange}
			renderNextButton={renderNextButton}
			renderPrevButton={renderPreviousButton}
			{...useCarouselSize()}
		>
			{children}
		</CarouselWithStaticVisibleSlots>
	);
};

export default Carousel;
